import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RecentTrackedSets } from "@/components/track/recent/RecentTrackedSets";
import { AudioInputTrack } from "@/components/track/audio/AudioInputTrack";
// import { VoiceRecorder } from "@/components/track/audio/VoiceRecorder";
import { setApi } from "@/api/setApi";
import { UserHeader } from "@/components/track/audio/UserHeader";
import { useToastHandler } from "@/hooks/useToastHandler";
import PageTitleScript from "@/components/MetaScript";
import { AudioExampleSlides } from "@/components/track/ExampleSlides/AudioExampleSlides";
import { useRefetchUserInfo } from "@/hooks/useUserInfo";
import { useInvalidateResources } from "@/hooks/useInvalidateResources";

export function TrackPage() {
  const navigate = useNavigate();
  const invalidate = useInvalidateResources();
  const refetchUserInfo = useRefetchUserInfo();

  const { handleError, handleSuccess } = useToastHandler();

  // Ref to prevent multiple concurrent processing attempts
  const isSendingAudioToServerRef = useRef(false);

  // Define the callback function for when recording completes
  const handleRecordingComplete = async (audioBlob: Blob) => {
    // Guard to ensure trackNewSetByAudio is only called once concurrently
    if (isSendingAudioToServerRef.current) return;
    isSendingAudioToServerRef.current = true;

    // play the audio in a low volume
    const audio = new Audio(URL.createObjectURL(audioBlob));
    audio.volume = 0.1;
    audio.play();
    try {
      console.log("Sending audio to server...");
      const addedSetResource = await setApi.trackNewSetByAudio(audioBlob);
      // Refresh the recent sets after successful processing

      invalidate([
        "/api/sets/recent",
        "/api/exercises/tracked",
        "/api/workouts",
        "/api/stats/overview",
      ]);
      refetchUserInfo();

      handleSuccess("New set added successfully!", 1000);
      navigate("/workouts/date/" + addedSetResource.claimed_train_local_date);
    } catch (error) {
      // force refresh this page in 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);

      handleError(error as unknown); // Use the existing handleError from useToastHandler
    } finally {
      console.log("finally triggered");
      // Reset the guard after navigation or error, allowing new submissions
      setTimeout(() => {
        isSendingAudioToServerRef.current = false;
      }, 1000); // Keep a small delay to prevent rapid re-submissions
    }
  };

  // Define the callback function for handling audio errors
  const handleAudioError = (error: unknown) => {
    handleError(error); // Use the existing handleError from useToastHandler
    // Ensure the guard is reset even if onError is called
    isSendingAudioToServerRef.current = false;
  };

  return (
    <div className="flex flex-col">
      <PageTitleScript title="Track Workout" />
      {/* Header */}
      <UserHeader />

      <div className="w-full flex flex-col items-center">
        {/* Voice input button */}
        <div className="grow w-full">
          <AudioInputTrack
            onRecordingComplete={handleRecordingComplete}
            onError={handleAudioError}
          />
        </div>

        {/* Audio Example Slides */}
        <AudioExampleSlides />

        {/* Recent Tracked Sets Section */}
        <RecentTrackedSets />
      </div>
    </div>
  );
}
